import React, { useEffect, useState } from 'react';
import './index.scss';
import {
    BoxedRow,
    ButtonFixedFooterLayout,
    ButtonLink,
    ButtonPrimary,
    Callout,
    EmptyState,
    IconCalendarRegular,
    IconChevronLeftRegular,
    IconInformationUserRegular,
    NavigationBar,
    RadioButton,
    RadioGroup,
    Tag,
    Text2,
    Text3,
    Text6,
    ThemeVariant,
    skinVars,
    useWindowSize,
} from '@telefonica/mistica';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TrackingEvents } from '../../../../../../utils/dataTransformation/tracking';
import { isWebviewAndroid, isWebviewIOS } from '../../../../../../utils/navigation/controlDevices';
import navigateParams from '../../../../../../utils/navigation/navigateParams';
import { Slot } from '../../../../../../models/Orders';
import { retrieveModifyAppointmentInfo, saveModifyAppointmentSelectedSlot } from '../../../appointmentStorage';
import { getWeekDay, getIntervalText, getDate } from '../../../slotUtils';
import BreadCrumbsWrapper from '../../../../../../components/BreadcrumbsWrapper';

export const ModifyAppointment: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { trackingScreenView } = new TrackingEvents();
    const size = useWindowSize();
    const [orderDescription, setOrderDescription] = useState<string>('');
    const [firstSlots, setFirstSlots] = useState<Slot[]>([]);
    const [selectedSlot, setSelectedSlot] = useState<Slot>();
    const [params] = useSearchParams();

    useEffect(() => {
        const { orderDescription, slots } = retrieveModifyAppointmentInfo();
        setOrderDescription(orderDescription);
        setFirstSlots(slots.slice(0, 5)); // Display only first 5 slots
        if (!(isWebviewAndroid || isWebviewIOS)) {
            trackingScreenView();
        }
    }, []);

    const sendToNative = (url: string, type: string, title: string) => {
        const queryParams = {};
        const storage = {};
        if (navigateParams(type, url, queryParams, storage, title)) {
            if (type === 'modeExternal') {
                window.location.href = url;
            } else {
                navigate(`${url}?id=${params.get('id')}`);
            }
        }
    };

    return (
        <div className='modifyAppointment-page'>
            <div className='modifyAppointment-page__header' style={{ background: skinVars.colors.backgroundBrand }}>
                {!(isWebviewAndroid || isWebviewIOS) && (
                    <>
                        <div className='modifyAppointment-page__header-navigationBar'>
                            <NavigationBar
                                isInverse={true}
                                onBack={() => {
                                    navigate(-1);
                                }}
                                title={t('modifyAppointment')}
                            />
                        </div>
                        <div className='modifyAppointment-page__header-return'>
                            <div
                                className='modifyAppointment-page__header-return-cursor'
                                onClick={() => {
                                    navigate(-1);
                                }}
                            >
                                <div className='modifyAppointment-page__header-return-cursor-icon'>
                                    <IconChevronLeftRegular color={skinVars.colors.appBarBackground} size={20} />
                                </div>
                                <Text2 medium color={skinVars.colors.appBarBackground}>
                                    {t('returnResponsive')}
                                </Text2>
                            </div>
                        </div>
                    </>
                )}
                <div className='modifyAppointment-page__header-breadcrumbs'>
                    <ThemeVariant isInverse>
                        <BreadCrumbsWrapper
                            title={t('modifyAppointment')}
                            breadcrumbs={[
                                { title: t('profileOrders'), url: '/profile/orders' },
                                { title: t('orderDetail'), url: `/order-detail?id=${params.get('id')}` },
                            ]}
                        />
                    </ThemeVariant>
                </div>
                {((firstSlots?.length === 0 && size.width >= 1024) || firstSlots?.length > 0) && (
                    <div className='modifyAppointment-page__header-product'>
                        <Text6 color={skinVars.colors.textPrimaryInverse}>{orderDescription}</Text6>
                    </div>
                )}
            </div>
            <div className='modifyAppointment-page__content'>
                {firstSlots?.length > 0 ? (
                    size.width >= 1024 ? (
                        <>
                            <div className='modifyAppointment-page__content-subtitle'>
                                <Text3 regular>{t('chooseInstallationDate')}</Text3>
                            </div>
                            <RadioGroup name={'schedules'} onChange={(value) => setSelectedSlot(JSON.parse(value))}>
                                <div className='modifyAppointment-page__content-radioB'>
                                    {firstSlots?.map((item, index) => {
                                        return (
                                            <div className='modifyAppointment-page__content-radioB-rows' key={index}>
                                                <BoxedRow
                                                    title={''}
                                                    extra={
                                                        <div className='modifyAppointment-page__content-radioB-rows-extra'>
                                                            <div className='modifyAppointment-page__content-radioB-rows-extra-tag'>
                                                                <Tag type='active'>{getWeekDay(item)}</Tag>
                                                            </div>
                                                            <div className='modifyAppointment-page__content-radioB-rows-extra-hour'>
                                                                <Text3 regular>{getIntervalText(item)}</Text3>
                                                            </div>

                                                            <Text2 regular color={skinVars.colors.textSecondary}>
                                                                {getDate(item)}
                                                            </Text2>
                                                        </div>
                                                    }
                                                    right={<RadioButton value={JSON.stringify(item)}></RadioButton>}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            </RadioGroup>
                            <div className='modifyAppointment-page__content-buttonLink'>
                                <ButtonLink
                                    onPress={() => {
                                        sendToNative(
                                            '/order-detail/modify-appointment-week',
                                            'modeFlow-backgroundBlue',
                                            t('modifyAppointment'),
                                        );
                                    }}
                                >
                                    {t('seeMoreSchedules')}
                                </ButtonLink>
                            </div>
                            <div className='modifyAppointment-page__content-callout'>
                                <Callout
                                    icon={<IconInformationUserRegular color={skinVars.colors.brand} />}
                                    title={t('needHelp')}
                                    description={t('descriptionCalloutModifyAppointmentDesktop')}
                                />
                            </div>
                            <div className='modifyAppointment-page__content-buttons'>
                                <ButtonPrimary
                                    disabled={selectedSlot === undefined}
                                    onPress={() => {
                                        saveModifyAppointmentSelectedSlot(selectedSlot!);
                                        sendToNative(
                                            '/order-detail/modify-appointment/confirm',
                                            'modeFlow-backgroundBlue',
                                            t('modifyAppointment'),
                                        );
                                    }}
                                >
                                    {t('continue')}
                                </ButtonPrimary>
                            </div>
                        </>
                    ) : (
                        <ButtonFixedFooterLayout
                            button={
                                <ButtonPrimary
                                    disabled={selectedSlot === undefined}
                                    onPress={() => {
                                        saveModifyAppointmentSelectedSlot(selectedSlot!);
                                        sendToNative(
                                            '/order-detail/modify-appointment/confirm',
                                            'modeFlow-backgroundBlue',
                                            t('modifyAppointment'),
                                        );
                                    }}
                                >
                                    {t('continue')}
                                </ButtonPrimary>
                            }
                        >
                            <div className='modifyAppointment-page__content-subtitle'>
                                <Text3 regular>{t('chooseInstallationDate')}</Text3>
                            </div>
                            <RadioGroup name={'schedules'} onChange={(value) => setSelectedSlot(JSON.parse(value))}>
                                <div className='modifyAppointment-page__content-radioB'>
                                    {firstSlots?.map((item, index) => {
                                        return (
                                            <div className='modifyAppointment-page__content-radioB-rows' key={index}>
                                                <BoxedRow
                                                    title={''}
                                                    extra={
                                                        <div className='modifyAppointment-page__content-radioB-rows-extra'>
                                                            <div className='modifyAppointment-page__content-radioB-rows-extra-tag'>
                                                                <Tag type='active'>
                                                                    {getWeekDay(item).charAt(0).toUpperCase() +
                                                                        getWeekDay(item).slice(1)}
                                                                </Tag>
                                                            </div>
                                                            <div className='modifyAppointment-page__content-radioB-rows-extra-hour'>
                                                                <Text3 regular>{getIntervalText(item)}</Text3>
                                                            </div>
                                                            <Text2 regular color={skinVars.colors.textSecondary}>
                                                                {getDate(item)}
                                                            </Text2>
                                                        </div>
                                                    }
                                                    right={<RadioButton value={JSON.stringify(item)}></RadioButton>}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            </RadioGroup>
                            <div className='modifyAppointment-page__content-buttonLink'>
                                <ButtonLink
                                    onPress={() => {
                                        sendToNative(
                                            '/order-detail/modify-appointment-week',
                                            'modeFlow-backgroundBlue',
                                            t('modifyAppointment'),
                                        );
                                    }}
                                >
                                    {t('seeMoreSchedules')}
                                </ButtonLink>
                            </div>
                            <div className='modifyAppointment-page__content-callout'>
                                <Callout
                                    icon={<IconInformationUserRegular color={skinVars.colors.brand} />}
                                    title={t('needHelp')}
                                    description={t('descriptionCalloutModifyAppointmentMobile')}
                                    buttonLink={
                                        <ButtonLink
                                            onPress={() => {
                                                sendToNative('tel://1551', 'modeExternal', '');
                                            }}
                                        >
                                            {t('contentModalHelpWithAccessBtnCard')}
                                        </ButtonLink>
                                    }
                                />
                            </div>
                        </ButtonFixedFooterLayout>
                    )
                ) : (
                    <div className='modifyAppointment-page__content-emptyState'>
                        <EmptyState
                            icon={<IconCalendarRegular color={skinVars.colors.brand} size={64} />}
                            title={t('titleEmptyStateNoDate')}
                            description={t('descriptionEmptyStateNoDate')}
                        ></EmptyState>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ModifyAppointment;
